import React, { useState, useEffect, useCallback } from 'react';
import {
  Send,
  Eye,
  Plus,
  MoreVertical,
  Loader2,
  FileCheck,
  ExternalLink,
  Copy,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { SupabasePreApprovalService } from '@/services/supabasePreApprovalService';
import { PreApprovalSendModal } from '@/components/pre-approval/PreApprovalSendModal';
import type { PreApprovalData } from '@/types/pre-approval';
import type { SimpleContact } from '@/services/contactService-simple';
import { formatCurrency } from '@/utils/formatCurrency';
import { openWindow } from '@/utils/windowOpenHelper';
import { createComponentLogger } from '@/utils/logger';
import { createPreApprovalCloneDraft } from '@/utils/pre-approval/cloneSanitizer';

const log = createComponentLogger('ContactPreApprovalsPanel', 'contacts');

interface ContactPreApprovalsPanelProps {
  contactId: string;
  contact: SimpleContact;
  onCreatePreApproval: (prefilledData: {
    borrowerInfo: {
      salutation?: string;
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    };
    contactId: string;
  }) => void;
}

/**
 * Panel for displaying and managing pre-approvals for a specific contact
 * Used in the ContactDetailPage Pre-Approvals tab
 */
export const ContactPreApprovalsPanel: React.FC<ContactPreApprovalsPanelProps> = ({
  contactId,
  contact,
  onCreatePreApproval
}) => {
  const [preApprovals, setPreApprovals] = useState<PreApprovalData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedPreApproval, setSelectedPreApproval] = useState<PreApprovalData | null>(null);
  const [isResend, setIsResend] = useState(false);

  /**
   * Load pre-approvals for this contact
   */
  const loadPreApprovals = useCallback(async () => {
    setIsLoading(true);
    try {
      const allPreApprovals = await SupabasePreApprovalService.getAllPreApprovals();

      // Filter to this contact's pre-approvals
      // Match by contact_id OR by borrower email (for existing pre-approvals without contact_id)
      const contactPreApprovals = allPreApprovals.filter(pa => {
        // Match by contact_id if set
        if (pa.contactId === contactId) {
return true;
}

        // Match by borrower email
        const borrowerEmail = pa.borrowerInfo?.email?.toLowerCase();
        const contactEmail = contact.email?.toLowerCase();
        if (borrowerEmail && contactEmail && borrowerEmail === contactEmail) {
return true;
}

        return false;
      });

      // Sort by created date descending
      contactPreApprovals.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      // Fetch accurate view counts from events table (view_count column is deprecated)
      const preApprovalsWithViewCounts = await Promise.all(
        contactPreApprovals.map(async (pa) => {
          const viewCount = await SupabasePreApprovalService.getViewCount(pa.id);
          return { ...pa, viewCount };
        })
      );

      setPreApprovals(preApprovalsWithViewCounts);
    } catch (error) {
      log.error('Failed to load pre-approvals', { contactId }, error as Error);
      toast.error('Failed to load pre-approvals');
    } finally {
      setIsLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    loadPreApprovals();
  }, [loadPreApprovals]);

  /**
   * Handle creating a new pre-approval for this contact
   */
  const handleCreateNew = () => {
    onCreatePreApproval({
      borrowerInfo: {
        salutation: contact.title,
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email || '',
        phone: contact.mobilePhone || contact.homePhone || contact.workPhone || ''
      },
      contactId: contact.id
    });
  };

  /**
   * Handle viewing a pre-approval preview
   */
  const handleView = (preApproval: PreApprovalData) => {
    const accessUrl = `${window.location.origin}/pre-approval/${preApproval.id}/preview?lo=true`;
    openWindow(accessUrl);
  };

  /**
   * Handle sending/resending a pre-approval
   */
  const handleSend = async (preApproval: PreApprovalData, resend: boolean = false) => {
    try {
      const fullPreApproval = await SupabasePreApprovalService.getPreApprovalById(preApproval.id);
      if (fullPreApproval) {
        setSelectedPreApproval(fullPreApproval);
        setIsResend(resend);
        setShowSendModal(true);
      } else {
        toast.error('Pre-approval not found');
      }
    } catch (error) {
      log.error('Failed to load pre-approval for sending', { preApprovalId: preApproval.id }, error as Error);
      toast.error('Failed to load pre-approval');
    }
  };

  /**
   * Handle cloning a pre-approval
   */
  const handleClone = async (preApproval: PreApprovalData) => {
    try {
      const fullPreApproval = await SupabasePreApprovalService.getPreApprovalById(preApproval.id);
      if (!fullPreApproval) {
        toast.error('Pre-approval not found');
        return;
      }

      const cloned = createPreApprovalCloneDraft(fullPreApproval);

      await SupabasePreApprovalService.savePreApproval(cloned);
      await loadPreApprovals();
      toast.success('Pre-approval cloned successfully');
    } catch (error) {
      log.error('Failed to clone pre-approval', { preApprovalId: preApproval.id }, error as Error);
      toast.error('Failed to clone pre-approval');
    }
  };

  /**
   * Get status badge with appropriate styling
   */
  const getStatusBadge = (status: PreApprovalData['status']) => {
    const statusConfig = {
      draft: { label: 'Draft', className: 'bg-gray-100 text-gray-700' },
      sent: { label: 'Sent', className: 'bg-blue-100 text-blue-700' },
      viewed: { label: 'Viewed', className: 'bg-green-100 text-green-700' },
      accepted: { label: 'Accepted', className: 'bg-emerald-100 text-emerald-700' },
      expired: { label: 'Expired', className: 'bg-red-100 text-red-700' }
    };

    const config = statusConfig[status];
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string) => {
    if (!dateString) {
return '-';
}
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${day}/${year}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading pre-approvals...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-medium">Pre-Approvals</h3>
          <Badge variant="secondary">{preApprovals.length}</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadPreApprovals}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={handleCreateNew} className="bg-[#8B5CF6] hover:bg-[#7C3AED]">
            <Plus className="h-4 w-4 mr-2" />
            Send Pre-Approval
          </Button>
        </div>
      </div>

      {/* Pre-Approvals List */}
      {preApprovals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileCheck className="h-12 w-12 text-muted-foreground mb-4" />
            <h4 className="text-lg font-medium mb-2">No Pre-Approvals Yet</h4>
            <p className="text-muted-foreground text-center mb-4">
              Send a pre-approval letter to {contact.firstName} {contact.lastName}
            </p>
            <Button onClick={handleCreateNew} className="bg-[#8B5CF6] hover:bg-[#7C3AED]">
              <Plus className="h-4 w-4 mr-2" />
              Send Pre-Approval
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Status</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {preApprovals.map((preApproval) => (
                  <TableRow key={preApproval.id}>
                    <TableCell>{getStatusBadge(preApproval.status)}</TableCell>
                    <TableCell className="font-medium">
                      {formatCurrency(
                        preApproval.loanDetails?.preApprovalAmount ||
                        preApproval.loanDetails?.loanAmount ||
                        0
                      )}
                    </TableCell>
                    <TableCell>{formatDate(preApproval.createdAt)}</TableCell>
                    <TableCell>{formatDate(preApproval.sentAt)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3 text-muted-foreground" />
                        {preApproval.viewCount || 0}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleView(preApproval)}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          {preApproval.status === 'draft' ? (
                            <DropdownMenuItem onClick={() => handleSend(preApproval, false)}>
                              <Send className="h-4 w-4 mr-2" />
                              Send
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleSend(preApproval, true)}>
                              <Send className="h-4 w-4 mr-2" />
                              Resend
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleClone(preApproval)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Clone
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Send Modal */}
      {selectedPreApproval && (
        <PreApprovalSendModal
          isOpen={showSendModal}
          onClose={() => {
            setShowSendModal(false);
            setSelectedPreApproval(null);
            setIsResend(false);
          }}
          preApproval={selectedPreApproval}
          onSend={() => {
            setShowSendModal(false);
            setSelectedPreApproval(null);
            setIsResend(false);
            loadPreApprovals();
            toast.success(isResend ? 'Pre-approval resent!' : 'Pre-approval sent!');
          }}
          isResend={isResend}
        />
      )}
    </div>
  );
};

export default ContactPreApprovalsPanel;
